import find from 'lodash-es/find'
import filter from 'lodash-es/filter'
import sortBy from 'lodash-es/sortBy'
import groupBy from 'lodash-es/groupBy'
import fromPairs from 'lodash-es/fromPairs'

import {
  PROGRAM_KIND_POINTS_VIA_PRICE,
  PROGRAM_KIND_POINTS_BY_PURCHASE,
  PROGRAM_KIND_STAMPS,
  PROGRAM_KIND_COLLECTOR,
  PROGRAM_KIND_PUZZLE,
  PROGRAM_KIND_CASHBACK,
  ALL_PROGRAM_GENERIC_KINDS
} from '~/store/program/state.js'

export const unauthorizedPrograms = (state, _, rootState) => {
  if (!state.declaration) return []
  if (!rootState.user.loyaltyPrograms) return []

  const unauthorizedProgramIdentifiers =
    state.declaration.details.on_dupe_program_identifiers

  if (!unauthorizedProgramIdentifiers) {
    return []
  }

  return rootState.user.loyaltyPrograms.filter((program) =>
    unauthorizedProgramIdentifiers.includes(program.identifier)
  )
}

export const programs = (state, _, rootState) => {
  if (!state.declaration) return []
  if (!rootState.user.loyaltyPrograms) return []

  const unauthorizedProgramIdentifiers =
    state.declaration.details.on_dupe_program_identifiers

  if (!unauthorizedProgramIdentifiers) {
    return rootState.user.loyaltyPrograms
  }

  return rootState.user.loyaltyPrograms.filter(
    (program) => !unauthorizedProgramIdentifiers.includes(program.identifier)
  )
}

export const allowedPrograms = (state, getters) => {
  switch (state.declaration.details.has_independant_loyalty) {
    case true:
      return filter(getters.programs, (program) =>
        state.declaration.details.allowed_loyalty_program_identifiers.includes(
          program.identifier
        )
      )
    case false:
      return filter(
        getters.programs,
        (program) =>
          !state.declaration.details.forbidden_loyalty_program_identifiers.includes(
            program.identifier
          )
      )
    default:
      return []
  }
}

export const availablePrograms = (_, getters) =>
  filter(getters.allowedPrograms, (program) => program.is_active)

export const availableProgramIdentifiers = (_, getters) =>
  getters.availablePrograms.map((p) => p.identifier)

export const availableProgramsByKind = (_, getters) =>
  groupBy(getters.availablePrograms, 'kind')

export const availableGenericPrograms = (_, getters) =>
  filter(getters.availablePrograms, (p) =>
    ALL_PROGRAM_GENERIC_KINDS.includes(p.kind)
  )

export const availableProgramsForPoints = (_, getters) =>
  (getters.availableProgramsByKind[PROGRAM_KIND_POINTS_VIA_PRICE] || []).concat(
    getters.availableProgramsByKind[PROGRAM_KIND_POINTS_BY_PURCHASE] || []
  )

export const availableProgramsForCashback = (_, getters) =>
  getters.availableProgramsByKind[PROGRAM_KIND_CASHBACK] || []

export const availableProgramsForStamps = (_, getters) =>
  getters.availableProgramsByKind[PROGRAM_KIND_STAMPS] || []

export const availableProgramsForPuzzlePieces = (_, getters) =>
  getters.availableProgramsByKind[PROGRAM_KIND_PUZZLE] || []

export const availableProgramsForCollectors = (_, getters) =>
  getters.availableProgramsByKind[PROGRAM_KIND_COLLECTOR] || []

export const anyAvailableProgramForPoints = (_, getters) =>
  getters.availableProgramsForPoints.length > 0

export const anyAvailableProgramForCollectors = (_, getters) =>
  getters.availableProgramsForCollectors.length > 0

export const noAvailablePrograms = (_, getters) =>
  getters.availablePrograms === 0

export const anyAvailablePrograms = (_, getters) =>
  getters.availablePrograms.length > 0

export const programsExpiredForPoints = (_, getters) =>
  filter(
    getters.allowedPrograms,
    (program) =>
      program.kind === PROGRAM_KIND_POINTS_VIA_PRICE && !program.is_active
  )

export const programsExpiredForPointsWithEarnedCoupons = (_, getters) =>
  filter(
    getters.programsExpiredForPoints,
    (program) => program.data.earned_coupons.length > 0
  )

export const anyProgramsExpiredForPointsWithEarnedCoupons = (_, getters) =>
  getters.programsExpiredForPointsWithEarnedCoupons.length > 0

export const availablePuzzleMissionsToAddOnDeclaration = (_, getters) => (
  programIdentifier
) => {
  if (getters.availableProgramsForPuzzlePieces.length === 0) return []

  const program = find(
    getters.availableProgramsForPuzzlePieces,
    (p) => p.identifier === programIdentifier
  )
  if (!program) return []

  return filter(
    program.kind_data.puzzle_missions,
    (pm) =>
      pm.can_be_added_on_declaration &&
      (pm.remaining_times === null || pm.remaining_times > 0)
  )
}

export const declarationIsRejected = (state) =>
  state.declaration.is_eligible === false

export const declarationIsNotRejected = (_state, getters) =>
  !getters.declarationIsRejected

export const hasOnlyOneItem = (_state, getters) =>
  getters.availableGenericPrograms.length +
    getters.availableProgramsForCollectors.length +
    getters.programsExpiredForPointsWithEarnedCoupons.length ===
  1

export const hasDupes = (state) => state.declaration.details.dupes.length > 0

export const dupesWithLoyaltyDetails = (state) =>
  fromPairs(
    sortBy(
      state.declaration.details.dupes.map((dupe) => [
        dupe.identifier,
        state.declaration.details.program_identifiers_by_dupe_identifiers[
          dupe.identifier
        ] || []
      ]),
      ([dupeIdentifier, programIdentifiers]) => [
        programIdentifiers.length,
        dupeIdentifier
      ]
    )
  )
